/**
 * API service for AI Agent frontend
 * Handles token-based authentication and domain validation
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://universal-ai-agent-backend.diypa571.workers.dev/api'

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

export interface DomainValidationResponse {
  isValid: boolean
  companyId?: string
  companyName?: string
  message?: string
}

export interface CompanyInfo {
  id: string
  name: string
  domain: string
  approvedDomains: string[]
}

class ApiService {
  private baseUrl: string

  constructor() {
    this.baseUrl = API_BASE_URL
  }

  /**
   * Validate domain and token
   */
  async validateDomain(token: string, domain: string): Promise<DomainValidationResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/validate-domain`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, domain }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Validation failed' }))
        return {
          isValid: false,
          message: error.error || error.message || 'Domain validation failed',
        }
      }

      const data = await response.json()
      return {
        isValid: data.success || data.isValid || false,
        companyId: data.data?.companyId || data.companyId,
        companyName: data.data?.companyName || data.companyName,
        message: data.message || data.data?.message,
      }
    } catch (error) {
      console.error('Domain validation error:', error)
      return {
        isValid: false,
        message: 'Failed to validate domain. Please check your connection.',
      }
    }
  }

  /**
   * Get company information by token
   */
  async getCompanyInfo(token: string): Promise<ApiResponse<CompanyInfo>> {
    try {
      const response = await fetch(`${this.baseUrl}/company/info`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to fetch company info' }))
        return {
          success: false,
          error: error.error || error.message || 'Failed to fetch company information',
        }
      }

      const data = await response.json()
      return {
        success: true,
        data: data.data || data,
      }
    } catch (error) {
      console.error('Get company info error:', error)
      return {
        success: false,
        error: 'Failed to fetch company information',
      }
    }
  }

  /**
   * Send chat message to AI agent with conversation history
   */
  async sendMessage(
    token: string, 
    message: string, 
    domain: string, 
    agentId?: string, 
    agentName?: string,
    conversationHistory?: Array<{ role: 'user' | 'assistant', content: string }>
  ): Promise<ApiResponse<{ response: string; conversationId?: string; messageId?: string }>> {
    try {
      const response = await fetch(`${this.baseUrl}/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          message, 
          domain, 
          agentId, 
          agentName,
          conversationHistory: conversationHistory || []
        }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to send message' }))
        return {
          success: false,
          error: error.error || error.message || 'Failed to send message',
        }
      }

      const data = await response.json()
      return {
        success: true,
        data: data.data || { response: data.response || data.message || '' },
      }
    } catch (error) {
      console.error('Send message error:', error)
      return {
        success: false,
        error: 'Failed to send message. Please try again.',
      }
    }
  }

  /**
   * Submit feedback for a message (thumbs up/down)
   */
  async submitFeedback(
    messageId: string,
    conversationId: string,
    feedbackType: 'positive' | 'negative',
    comment?: string
  ): Promise<ApiResponse<void>> {
    const feedbackUrl = `${this.baseUrl}/feedback`
    console.log('Sending feedback to:', feedbackUrl)
    
    try {
      const body = {
        messageId,
        conversationId,
        feedbackType,
        comment
      }
      console.log('Feedback payload:', body)
      
      const response = await fetch(feedbackUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })

      console.log('Feedback response status:', response.status)
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to submit feedback' }))
        console.error('Feedback error response:', error)
        return {
          success: false,
          error: error.error || error.message || 'Failed to submit feedback',
        }
      }

      const data = await response.json()
      console.log('Feedback success response:', data)
      return { success: true }
    } catch (error) {
      console.error('Submit feedback error:', error)
      return {
        success: false,
        error: 'Failed to submit feedback',
      }
    }
  }
}

export const apiService = new ApiService()

